import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { InterviewerSettings } from './InterviewerSettingsStep';
import InterviewerSettingsStep from './InterviewerSettingsStep';
import CustomAssessmentQuestionsStep from './CustomAssessmentQuestionsStep';

export interface CustomAssessmentConfigProps {
  onSubmit: (config: CustomAssessmentConfig) => void;
  selectedSkills?: string[];
  role?: string;
  level?: 'entry' | 'mid' | 'senior';
  initialGeneralQuestions?: string[];
  initialSkillQuestions?: Record<string, string[]>;
}

export interface CustomAssessmentConfig {
  questions: string[];
  language: string;
  accent: string;
  voiceId: string;
  skillQuestions?: Record<string, string[]>;
}

// Add a step type
type CustomAssessmentStep = 'questions' | 'settings';

const CustomAssessmentConfig: React.FC<CustomAssessmentConfigProps> = ({ 
  onSubmit, 
  selectedSkills = [],
  role = '',
  level = 'mid',
  initialGeneralQuestions = [''],
  initialSkillQuestions = {}
}) => {
  // Store the general and skill-specific questions, initialized with values if provided
  const [generalQuestions, setGeneralQuestions] = useState<string[]>(initialGeneralQuestions);
  
  // Initialize skillQuestions with initialSkillQuestions or empty arrays for each skill
  const [skillQuestions, setSkillQuestions] = useState<Record<string, string[]>>(() => {
    // Start with any initial skill questions
    const questions = { ...initialSkillQuestions };
    
    // Make sure every selected skill has an entry
    selectedSkills.forEach(skill => {
      if (!questions[skill]) {
        questions[skill] = [''];
      }
    });
    
    return questions;
  });
  
  // Add state for the current step
  const [currentStep, setCurrentStep] = useState<CustomAssessmentStep>('questions');
  
  // Settings state with default values
  const [interviewerSettings, setInterviewerSettings] = useState<InterviewerSettings>({
    language: 'en',
    accent: 'us',
    voiceId: ''
  });

  // Handle submission of the question step
  const handleQuestionsStepContinue = (generalQuestions: string[], skillQuestions: Record<string, string[]>) => {
    setGeneralQuestions(generalQuestions);
    setSkillQuestions(skillQuestions);
    setCurrentStep('settings');
  };

  // Handle going back to the questions step
  const handleBackToQuestions = () => {
    setCurrentStep('questions');
  };

  // Handle settings changes
  const handleSettingsChange = (settings: InterviewerSettings) => {
    setInterviewerSettings(settings);
  };

  // Final submit that combines the questions and settings
  const handleSubmit = () => {
    // Filter out empty questions
    const filteredGeneralQuestions = generalQuestions.filter(q => q.trim() !== '');
    
    // Filter out empty questions from each skill
    const filteredSkillQuestions: Record<string, string[]> = {};
    Object.entries(skillQuestions).forEach(([skill, questions]) => {
      filteredSkillQuestions[skill] = questions.filter(q => q.trim() !== '');
    });
    
    // Check if we have at least one question in total
    const totalQuestions = [
      ...filteredGeneralQuestions,
      ...Object.values(filteredSkillQuestions).flat()
    ];
    
    if (totalQuestions.length === 0) {
      toast.error("Please add at least one question");
      return;
    }
    
    // Validate that voiceId is set
    if (!interviewerSettings.voiceId) {
      toast.error("Voice configuration is not complete. Please select a language and accent.");
      return;
    }
    
    // Submit the final configuration with skill questions in skillQuestions
    // and keeping general questions in questions array
    onSubmit({
      questions: filteredGeneralQuestions,
      language: interviewerSettings.language,
      accent: interviewerSettings.accent,
      voiceId: interviewerSettings.voiceId,
      skillQuestions: filteredSkillQuestions
    });
  };

  return (
    <div>
      {currentStep === 'questions' ? (
        <CustomAssessmentQuestionsStep
          selectedSkills={selectedSkills}
          role={role}
          level={level}
          onContinue={handleQuestionsStepContinue}
          onBack={() => {}}
          initialGeneralQuestions={generalQuestions}
          initialSkillQuestions={skillQuestions}
        />
      ) : (
        <InterviewerSettingsStep
          settings={interviewerSettings}
          onSettingsChange={handleSettingsChange}
          onSubmit={handleSubmit}
          onBack={handleBackToQuestions}
          role={role}
          level={level}
        />
      )}
    </div>
  );
};

export default CustomAssessmentConfig; 